import { RecipeIngredientType } from "../../../config/enums/IngredientType.enum";
import { fixedColumns } from "../../../config/tableColConstant";
import { buildRecipeChartStylePayload } from "../recipeResultDetails/helpers/recipeImpactChart.style.helper";
import { getCompanyId, getCompanyName } from "../../../utils/cacheStorage";

export type NormalizedIngredient = {
  ingredientId: string;
  ingredientName: string;
  percentage: number;
  type: string;
  description?: string | null;
  status?: string | null;
  record?: string | null;
  allocationMethod?: string | null;
  databaseName?: string | null;
};

const SCALE = 100000;
const EPSILON = 1e-10;

const toInt = (n: number) => Math.trunc((n + EPSILON) * SCALE);
const fromInt = (n: number) => n / SCALE;

export const normalizeIngredients = (
  recipeData: any,
  staticRecipeIngredients: any[]
): NormalizedIngredient[] => {
  if (!recipeData) return [];

  const {
    calculatedProductSalesUnit = [],
    genericIngredient = [],
    calculatedIngredient = []
  } = recipeData;

  const ingredientCalculation = (
    ingredient: any,
    ingredientName: string,
    type: string,
    extras?: Pick<
      NormalizedIngredient,
      "description" | "status" | "record" | "allocationMethod" | "databaseName"
    >
  ): NormalizedIngredient => ({
    ingredientId: ingredient._id,
    ingredientName,
    percentage: ingredient.percentage,
    type,
    description: extras?.description ?? null,
    status: extras?.status ?? null,
    record: extras?.record ?? null,
    allocationMethod: extras?.allocationMethod ?? null,
    databaseName: extras?.databaseName ?? null
  });

  const calcProductIngredients = Array.isArray(calculatedProductSalesUnit)
    ? calculatedProductSalesUnit.map(ing =>
        ingredientCalculation(
          ing,
          `${ing.productName ?? ""} ${ing.salesUnitName ?? ""}`.trim(),
          RecipeIngredientType.OWN_PRODUCT
        )
      )
    : [];

  const genericIngredients = Array.isArray(genericIngredient)
    ? genericIngredient.map(ing => {
        const staticIngredient = staticRecipeIngredients?.find(
          (si: any) => String(si.key) === String(ing.ingredientId)
        );
        return ingredientCalculation(
          ing,
          staticIngredient?.text ?? "Unknown Ingredient",
          RecipeIngredientType.GENERIC_INGREDIENT,
          {
            description: staticIngredient?.description ?? null,
            status: staticIngredient?.status ?? null,
            record: staticIngredient?.record ?? null,
            allocationMethod: staticIngredient?.allocationMethod ?? null,
            databaseName: staticIngredient?.databaseName ?? null
          }
        );
      })
    : [];

  const calcIngredients = Array.isArray(calculatedIngredient)
    ? calculatedIngredient.map(ing =>
        ingredientCalculation(
          ing,
          ing.ingredientName ?? "Unnamed Ingredient",
          RecipeIngredientType.CALCULATED_INGREDIENT
        )
      )
    : [];

  return [...calcProductIngredients, ...genericIngredients, ...calcIngredients];
};

export const buildCombinedIngredients = (
  recipeData: any,
  variants: any[],
  staticRecipeIngredientList: any[]
) => {
  if (!recipeData?.originalRecipe) return [];

  const baseIngredients = normalizeIngredients(recipeData.originalRecipe, staticRecipeIngredientList);
  const variantIngredients = variants.flatMap(v =>
    normalizeIngredients(v, staticRecipeIngredientList)
  );

  const allIngredientsMap = new Map<string, NormalizedIngredient>();
  [...baseIngredients, ...variantIngredients].forEach(ing => {
    allIngredientsMap.set(ing.ingredientId, ing);
  });

  return Array.from(allIngredientsMap.values());
};

export const buildIngredientNames = (recipeData: any, staticRecipeIngredientList: any[]) => {
  if (!recipeData?.originalRecipe) return [];

  return normalizeIngredients(recipeData.originalRecipe, staticRecipeIngredientList).map(ing => ({
    ingredientId: ing.ingredientId,
    ingredientName: ing.ingredientName,
    type: ing.type,
    description: ing.description ?? null,
    status: ing.status ?? null,
    record: ing.record ?? null,
    allocationMethod: ing.allocationMethod ?? null,
    databaseName: ing.databaseName ?? null
  }));
};

type IngredientCategory =
  | "calculatedProductSalesUnit"
  | "genericIngredient"
  | "calculatedIngredient";

export const findOriginalIngredientId = (
  variantItem: any,
  originalRecipe: any,
  category: IngredientCategory
): string | undefined => {
  if (!variantItem || !originalRecipe) {
    return undefined;
  }

  if (category === "calculatedProductSalesUnit") {
    return originalRecipe.calculatedProductSalesUnit?.find(
      (item: any) =>
        String(item.productId) === String(variantItem.productId) &&
        String(item.salesUnitId) === String(variantItem.salesUnitId)
    )?._id;
  }

  if (category === "genericIngredient") {
    return originalRecipe.genericIngredient?.find(
      (item: any) => String(item.ingredientId) === String(variantItem.ingredientId)
    )?._id;
  }

  if (category === "calculatedIngredient") {
    return originalRecipe.calculatedIngredient?.find(
      (item: any) =>
        String(item.ingredientId) === String(variantItem.ingredientId) &&
        String(item.supplierId) === String(variantItem.supplierId)
    )?._id;
  }

  return undefined;
};

export const findVariantIngredientPercentage = (
  variant: any,
  originalIngredientId: string,
  originalRecipe: any
): number => {
  if (!variant || !originalRecipe) {
    return 0;
  }

  const ownProduct = originalRecipe.calculatedProductSalesUnit?.find(
    (item: any) => String(item._id) === String(originalIngredientId)
  );
  if (ownProduct) {
    const variantItem = variant.calculatedProductSalesUnit?.find(
      (item: any) =>
        String(item.productId) === String(ownProduct.productId) &&
        String(item.salesUnitId) === String(ownProduct.salesUnitId)
    );
    return variantItem?.percentage ?? 0;
  }

  const genericIngredient = originalRecipe.genericIngredient?.find(
    (item: any) => String(item._id) === String(originalIngredientId)
  );
  if (genericIngredient) {
    const variantItem = variant.genericIngredient?.find(
      (item: any) => String(item.ingredientId) === String(genericIngredient.ingredientId)
    );
    return variantItem?.percentage ?? 0;
  }

  const calculatedIngredient = originalRecipe.calculatedIngredient?.find(
    (item: any) => String(item._id) === String(originalIngredientId)
  );
  if (calculatedIngredient) {
    const variantItem = variant.calculatedIngredient?.find(
      (item: any) =>
        String(item.ingredientId) === String(calculatedIngredient.ingredientId) &&
        String(item.supplierId) === String(calculatedIngredient.supplierId)
    );
    return variantItem?.percentage ?? 0;
  }

  return 0;
};

export const buildFormDefaultValues = (
  recipeData: any,
  visibleIngredients: Array<{ ingredientId: string; type: string }>,
  variants: any[],
  staticRecipeIngredientList: any[]
) => {
  if (!recipeData?.originalRecipe) return {};

  const defaults: Record<string, number> = {};
  const originalNormalized = normalizeIngredients(
    recipeData.originalRecipe,
    staticRecipeIngredientList
  );

  visibleIngredients.forEach(ing => {
    const origIng = originalNormalized.find(
      item => String(item.ingredientId) === String(ing.ingredientId)
    );
    defaults[`original_${ing.ingredientId}`] = origIng?.percentage ?? 0;

    variants.forEach((variant, variantIdx) => {
      defaults[`variant_${ing.ingredientId}_${variantIdx}`] = findVariantIngredientPercentage(
        variant,
        ing.ingredientId,
        recipeData.originalRecipe
      );
    });
  });

  return defaults;
};

export const buildDynamicColumns = (variants: any[]) =>
  variants.map(v => ({
    id: v._id,
    title: v.variantName,
    variantId: v._id,
    notes: v.notes,
    custom: false,
    hasUpdatedIngredients:
      [
        ...(v.calculatedProductSalesUnit || []),
        ...(v.genericIngredient || []),
        ...(v.calculatedIngredient || []),
        ...(v.processes || [])
      ].some((item: any) => item.isUpdated === true) || v.isUpdated === true
  }));

type UpdatePercentagesOptions = {
  variantIdx: number;
  originalRecipe: any;
  category: IngredientCategory;
};

export const updatePercentages = (
  items: any[],
  formValues: any,
  options?: UpdatePercentagesOptions
) => {
  if (!Array.isArray(items)) return [];

  return items.map(ing => {
    let key: string;

    if (options) {
      const originalId = findOriginalIngredientId(ing, options.originalRecipe, options.category);
      key = `variant_${originalId}_${options.variantIdx}`;
    } else {
      key = `original_${ing._id}`;
    }

    return {
      ...ing,
      percentage: Number(formValues[key]) || 0
    };
  });
};

export const updateIngredientNames = (original: any, nameList: any[]) => {
  const findIngredientName = (id: any) => {
    const match = nameList.find(n => n.ingredientId === id);
    return match ? match.ingredientName : undefined;
  };

  const targetKeys = ["calculatedProductSalesUnit", "genericIngredient", "calculatedIngredient"];
  const updated = { ...original };

  for (const key of targetKeys) {
    if (Array.isArray(updated[key])) {
      updated[key] = updated[key].map((item: any) => {
        const id = item._id || item.ingredientId;
        const ingredientName = findIngredientName(id);
        return ingredientName ? { ...item, ingredientName } : item;
      });
    }
  }

  return updated;
};

export const buildFixedColumnsWithRealTimeUpdates = (recipeData: any, values: any) =>
  fixedColumns.map(col => {
    if (col.title === "Baseline (%)" && recipeData?.originalRecipe) {
      const originalRecipe = recipeData.originalRecipe;
      const allOriginalIngredients = [
        ...(originalRecipe.calculatedProductSalesUnit || []),
        ...(originalRecipe.genericIngredient || []),
        ...(originalRecipe.calculatedIngredient || []),
        ...(originalRecipe.processes || [])
      ];

      const hasBackendUpdatedIngredients =
        allOriginalIngredients.some((item: any) => item.isUpdated === true) ||
        originalRecipe.isUpdated === true;

      const hasRealTimeUpdates = allOriginalIngredients.some(ingredient => {
        const formKey = `original_${ingredient._id}`;
        const formValue = Number(values[formKey]) || 0;
        const originalValue = Number(ingredient.percentage) || 0;
        return formValue !== originalValue;
      });

      return {
        ...col,
        hasUpdatedIngredients: hasBackendUpdatedIngredients || hasRealTimeUpdates
      };
    }

    return col;
  });

export const buildDynamicColumnsWithRealTimeUpdates = (
  dynamicColumns: any[],
  variants: any[],
  values: any,
  originalRecipe: any
) =>
  dynamicColumns.map((col, variantIndex) => {
    const variant = variants[variantIndex];
    if (!variant || !originalRecipe) return col;

    const ingredientGroups: Array<{ items: any[]; category: IngredientCategory }> = [
      {
        items: variant.calculatedProductSalesUnit || [],
        category: "calculatedProductSalesUnit"
      },
      { items: variant.genericIngredient || [], category: "genericIngredient" },
      { items: variant.calculatedIngredient || [], category: "calculatedIngredient" }
    ];

    const hasRealTimeUpdates = ingredientGroups.some(({ items, category }) =>
      items.some(ingredient => {
        const originalId = findOriginalIngredientId(ingredient, originalRecipe, category);
        const formKey = `variant_${originalId}_${variantIndex}`;
        const formValue = Number(values[formKey]) || 0;
        const originalValue = Number(ingredient.percentage) || 0;
        return formValue !== originalValue;
      })
    );

    return {
      ...col,
      hasUpdatedIngredients: col.hasUpdatedIngredients || hasRealTimeUpdates
    };
  });

export const computeRecipeTotals = (
  values: any,
  dynamicColumnsWithRealTimeUpdates: any[],
  visibleIngredients: Array<{ ingredientId: string }>,
  variants: any[]
) => {
  const result = {
    original: 0,
    variants: Array(dynamicColumnsWithRealTimeUpdates.length).fill(0)
  };

  let originalSumInt = 0;
  const variantSumInts = Array(dynamicColumnsWithRealTimeUpdates.length).fill(0);

  visibleIngredients.forEach(ing => {
    const originalValue = Number(values[`original_${ing.ingredientId}`]) || 0;
    originalSumInt += toInt(originalValue);

    dynamicColumnsWithRealTimeUpdates.forEach((col, displayIdx) => {
      const actualVariantIdx = variants.findIndex(v => v._id === col.variantId);
      if (actualVariantIdx !== -1) {
        const variantValue =
          Number(values[`variant_${ing.ingredientId}_${actualVariantIdx}`]) || 0;
        variantSumInts[displayIdx] += toInt(variantValue);
      }
    });
  });

  result.original = fromInt(originalSumInt);
  result.variants = variantSumInts.map(fromInt);

  return result;
};

export const buildCalculatePayload = (recipeData: any, formValues: any) => ({
  _id: recipeData._id,
  recipeName: recipeData.recipeName,
  notes: recipeData.notes,
  originalRecipe: {
    ...recipeData.originalRecipe,
    calculatedProductSalesUnit: updatePercentages(
      recipeData.originalRecipe.calculatedProductSalesUnit,
      formValues
    ),
    genericIngredient: updatePercentages(recipeData.originalRecipe.genericIngredient, formValues),
    calculatedIngredient: updatePercentages(
      recipeData.originalRecipe.calculatedIngredient,
      formValues
    ),
    processes: recipeData.originalRecipe.processes
  },
  variants: recipeData.variants.map((variant: any, variantIdx: number) => ({
    ...variant,
    calculatedProductSalesUnit: updatePercentages(variant.calculatedProductSalesUnit, formValues, {
      variantIdx,
      originalRecipe: recipeData.originalRecipe,
      category: "calculatedProductSalesUnit"
    }),
    genericIngredient: updatePercentages(variant.genericIngredient, formValues, {
      variantIdx,
      originalRecipe: recipeData.originalRecipe,
      category: "genericIngredient"
    }),
    calculatedIngredient: updatePercentages(variant.calculatedIngredient, formValues, {
      variantIdx,
      originalRecipe: recipeData.originalRecipe,
      category: "calculatedIngredient"
    }),
    processes: variant.processes
  }))
});

export const buildDownloadPayload = ({
  recipeData,
  selectedVariants,
  recipeResults,
  impactDetails,
  productSize,
  combinedIngredients
}: {
  recipeData: any;
  selectedVariants: any[];
  recipeResults: any;
  impactDetails: any;
  productSize: number;
  combinedIngredients: NormalizedIngredient[];
}) => {
  const selectedVariantIds = selectedVariants.map(v => v.id);
  const filteredVariants =
    recipeData.variants?.filter((v: any) => selectedVariantIds.includes(v._id)) || [];
  const filteredVariantsImpacts =
    recipeResults?.variantstotalImpacts?.filter((vi: any) =>
      selectedVariantIds.includes(vi._id)
    ) || [];

  const chartStyle = buildRecipeChartStylePayload({
    recipeResults: recipeResults ?? undefined,
    originalRecipeData: recipeData,
    selectedVariants
  });

  const enrichedVariantsImpacts = filteredVariantsImpacts.map((variantImpact: any) => ({
    ...variantImpact,
    variantImpactBreakdown:
      chartStyle.enrichedVariantBreakdowns[variantImpact._id] ??
      variantImpact.variantImpactBreakdown
  }));

  return {
    recipeName: recipeData.recipeName,
    createdAt: recipeData.createdAt,
    updatedAt: recipeData.updatedAt,
    variants: filteredVariants,
    tenantId: getCompanyId(),
    companyName: getCompanyName(),
    originalImpact: impactDetails?.originalImpact || 0,
    productionSize: productSize ?? 1000,
    originalRecipeImpactBreakdown: chartStyle.enrichedOriginalBreakdown,
    originalRecipetotalImpacts: recipeResults?.originalRecipetotalImpacts || {},
    variantstotalImpacts: enrichedVariantsImpacts,
    originalRecipe: updateIngredientNames(recipeData.originalRecipe, combinedIngredients),
    chartLegendItems: chartStyle.legendItems,
    documentType: "pdf"
  };
};
